/**
 * PTE Academic — Learn & Revise (student-facing revision hub).
 *
 * Uses existing PTE_STRATEGY content from the backend. Each task type card
 * shows: What is this task? · What do I need to do? · Skill · Approach ·
 * Steps · Timing · Common mistakes · Tips · Start Practice CTA.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { pteApi } from '@/lib/pteApi';
import { BookOpen, ChevronRight, PlayCircle, Loader2, Target, Clock, AlertCircle, Sparkles } from 'lucide-react';

export default function PTELearnPage() {
  const { taskType } = useParams();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await pteApi.taskTypes();
        setTypes(r.items || []);
      } catch (_) {}
    })();
  }, []);

  const loadDetail = useCallback(async (key) => {
    setLoading(true);
    try {
      const r = await pteApi.strategy(key);
      setDetail({ key, ...r });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (taskType) loadDetail(taskType); else setDetail(null); }, [taskType, loadDetail]);

  const bySection = {
    speaking_writing: types.filter((t) => t.section === 'speaking_writing'),
    reading: types.filter((t) => t.section === 'reading'),
    listening: types.filter((t) => t.section === 'listening'),
  };

  if (detail) {
    const s = detail.strategy || {};
    const t = types.find((x) => x.key === detail.key);
    return (
      <div className="min-h-screen bg-slate-50" data-testid="pte-learn-detail">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-5">
          <button onClick={() => navigate('/resources/pte-academic/learn')}
            data-testid="learn-back"
            className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ChevronRight className="h-3 w-3 rotate-180" /> All task types
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">{t?.section?.replace('_', ' & ')}</div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-1">{t?.label || detail.key}</h1>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card icon={BookOpen} title="What is this task?" body={s.what} />
            <Card icon={Target} title="Skill being practised" body={s.skill} />
            <Card icon={Sparkles} title="What you see or hear" body={s.see_hear} />
            <Card icon={PlayCircle} title="What you do" body={s.do} />
          </div>
          {s.steps?.length > 0 && (
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="learn-steps">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Step-by-step approach</div>
              <ol className="space-y-2 list-decimal list-inside text-sm text-slate-800">
                {s.steps.map((st, i) => <li key={i}>{st}</li>)}
              </ol>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {s.timing && <Card icon={Clock} title="Timing" body={s.timing} testid="learn-timing" />}
            {s.approach && <Card icon={Target} title="Approach" body={s.approach} testid="learn-approach" />}
          </div>
          {s.mistakes?.length > 0 && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5" data-testid="learn-mistakes">
              <div className="text-xs uppercase tracking-widest text-amber-800 font-bold mb-2 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" /> Common mistakes
              </div>
              <ul className="space-y-1 text-sm text-slate-800">
                {s.mistakes.map((m, i) => <li key={i}>• {m}</li>)}
              </ul>
            </div>
          )}
          {s.tips?.length > 0 && (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5" data-testid="learn-tips">
              <div className="text-xs uppercase tracking-widest text-emerald-800 font-bold mb-2 flex items-center gap-1">
                <Sparkles className="h-3.5 w-3.5" /> Useful tips
              </div>
              <ul className="space-y-1 text-sm text-slate-800">
                {s.tips.map((tp, i) => <li key={i}>• {tp}</li>)}
              </ul>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <Link to={`/resources/pte-academic/practice/${detail.key}`} data-testid="learn-start-practice"
              className="px-6 py-3 rounded-full bg-slate-900 text-white font-bold text-sm inline-flex items-center gap-2 hover:bg-slate-800 transition">
              <PlayCircle className="h-4 w-4" /> Start practice
            </Link>
          </div>
          {loading && <div className="text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pte-learn-page">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <div>
          <Link to="/resources/pte-academic"
            className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ChevronRight className="h-3 w-3 rotate-180" /> PTE Academic
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-1">Learn &amp; Revise</h1>
          <p className="text-slate-600 text-sm md:text-base mt-1 max-w-2xl">
            Strategy notes for every PTE Academic task type — what it is, what you do, common mistakes and quick tips. Pick a task type to open the guide, then jump straight into practice.
          </p>
        </div>
        {[
          ['speaking_writing', 'Speaking & Writing'],
          ['reading', 'Reading'],
          ['listening', 'Listening'],
        ].map(([sec, label]) => (
          <div key={sec} data-testid={`learn-section-${sec}`}>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">{label}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {bySection[sec].map((t) => (
                <Link key={t.key} to={`/resources/pte-academic/learn/${t.key}`}
                  data-testid={`learn-card-${t.key}`}
                  className="group rounded-2xl bg-white border border-slate-200 hover:border-emerald-500 p-4 transition">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{t.skill}</div>
                  <div className="text-lg font-bold text-slate-900 mt-1">{t.label}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-emerald-700 text-sm font-semibold group-hover:gap-1.5 transition-all">
                    Open guide <ChevronRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Card = ({ icon: Icon, title, body, testid }) => body ? (
  <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center gap-1">
      <Icon className="h-3.5 w-3.5" /> {title}
    </div>
    <div className="text-sm text-slate-800">{body}</div>
  </div>
) : null;
